import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  BarController,
  DoughnutController,
  LineController,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip,
  Filler,
} from "chart.js"

Chart.register(
  BarController,
  DoughnutController,
  LineController,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip,
  Filler
)

const COLORS = [
  "rgba(59, 130, 246, 0.85)",
  "rgba(16, 185, 129, 0.85)",
  "rgba(245, 158, 11, 0.85)",
  "rgba(239, 68, 68, 0.85)",
  "rgba(139, 92, 246, 0.85)",
  "rgba(20, 184, 166, 0.85)",
  "rgba(236, 72, 153, 0.85)",
  "rgba(107, 114, 128, 0.85)",
]

// Ruby wday: 0=Sun … 6=Sat. Display Mon→Sun.
const HEATMAP_DOWS = [1, 2, 3, 4, 5, 6, 0]

export default class extends Controller {
  static targets = [
    "dailyHours",
    "statusDonut",
    "utilBar",
    "activeSpan",
    "mixDonut",
    "heatmap",
    "createdClosed",
    "typeBar",
    "transitionBar",
    "lineSeries",
    "roleBar",
  ]

  static values = {
    dailyLabels: Array,
    dailyHours: Array,
    statusLabels: Array,
    statusCounts: Array,
    utilLabels: Array,
    utilValues: Array,
    activeValues: Array,
    spanValues: Array,
    mixLabels: Array,
    mixValues: Array,
    heatmap: Object,
    created: Array,
    closed: Array,
    typeLabels: Array,
    typeCounts: Array,
    transitionLabels: Array,
    transitionCounts: Array,
    roleLabels: Array,
    roleCounts: Array,
    labels: Object,
  }

  connect() {
    this.charts = []
    this.renderAll()
  }

  disconnect() {
    this.charts.forEach((c) => c.destroy())
    this.charts = []
  }

  get L() {
    return this.labelsValue || {}
  }

  renderAll() {
    if (this.hasDailyHoursTarget) this.bar(this.dailyHoursTarget, this.dailyLabelsValue, this.dailyHoursValue, COLORS[0])
    if (this.hasStatusDonutTarget) this.donut(this.statusDonutTarget, this.statusLabelsValue, this.statusCountsValue)
    if (this.hasUtilBarTarget) this.bar(this.utilBarTarget, this.utilLabelsValue, this.utilValuesValue, COLORS[1])
    if (this.hasActiveSpanTarget) this.groupedBar(this.activeSpanTarget, this.utilLabelsValue, this.activeValuesValue, this.spanValuesValue)
    if (this.hasMixDonutTarget) this.donut(this.mixDonutTarget, this.mixLabelsValue, this.mixValuesValue)
    if (this.hasHeatmapTarget) this.renderHeatmap()
    if (this.hasCreatedClosedTarget) this.groupedCreatedClosed()
    if (this.hasTypeBarTarget) this.bar(this.typeBarTarget, this.typeLabelsValue, this.typeCountsValue, COLORS[2])
    if (this.hasTransitionBarTarget) this.bar(this.transitionBarTarget, this.transitionLabelsValue, this.transitionCountsValue, COLORS[4])
    if (this.hasLineSeriesTarget) this.line(this.lineSeriesTarget, this.dailyLabelsValue, this.closedValue, COLORS[0])
    if (this.hasRoleBarTarget) this.bar(this.roleBarTarget, this.roleLabelsValue, this.roleCountsValue, COLORS[5])
  }

  bar(canvas, labels, data, color) {
    this.charts.push(
      new Chart(canvas, {
        type: "bar",
        data: {
          labels,
          datasets: [{ data, backgroundColor: color, borderRadius: 4 }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { x: { ticks: { maxRotation: 45, autoSkip: true } }, y: { beginAtZero: true } },
        },
      })
    )
  }

  donut(canvas, labels, data) {
    this.charts.push(
      new Chart(canvas, {
        type: "doughnut",
        data: {
          labels,
          datasets: [{ data, backgroundColor: COLORS.slice(0, Math.max(labels.length, 1)) }],
        },
        options: { responsive: true, maintainAspectRatio: false },
      })
    )
  }

  line(canvas, labels, data, color) {
    this.charts.push(
      new Chart(canvas, {
        type: "line",
        data: {
          labels,
          datasets: [{
            data,
            borderColor: color,
            backgroundColor: color.replace("0.85", "0.15"),
            fill: true,
            tension: 0.3,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true } },
        },
      })
    )
  }

  groupedBar(canvas, labels, active, span) {
    this.charts.push(
      new Chart(canvas, {
        type: "bar",
        data: {
          labels,
          datasets: [
            { label: this.L.active || "Active", data: active, backgroundColor: COLORS[1] },
            { label: this.L.span || "Span", data: span, backgroundColor: COLORS[7] },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: { y: { beginAtZero: true } },
        },
      })
    )
  }

  groupedCreatedClosed() {
    this.charts.push(
      new Chart(this.createdClosedTarget, {
        type: "bar",
        data: {
          labels: this.dailyLabelsValue,
          datasets: [
            { label: this.L.created || "Created", data: this.createdValue, backgroundColor: COLORS[0] },
            { label: this.L.closed || "Closed", data: this.closedValue, backgroundColor: COLORS[1] },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: { y: { beginAtZero: true } },
        },
      })
    )
  }

  renderHeatmap() {
    const grid = this.heatmapValue || {}
    const hours = [...Array(24).keys()]
    const weekdays = this.L.weekdays || ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    const eventsWord = this.L.events || "events"
    const values = Object.values(grid).map(Number)
    const max = Math.max(0, ...values)

    const hourHeads = hours.map((h) => {
      const show = h % 3 === 0
      return `<th class="reports-heatmap__hour${show ? "" : " reports-heatmap__hour--muted"}">${show ? `${h}` : ""}</th>`
    }).join("")

    const rows = HEATMAP_DOWS.map((dow) => {
      const dayLabel = weekdays[dow] || String(dow)
      const cells = hours.map((h) => {
        const count = Number(grid[`${dow}-${h}`] || 0)
        const intensity = max > 0 ? count / max : 0
        const bg = count === 0
          ? "rgba(148, 163, 184, 0.12)"
          : `rgba(16, 185, 129, ${0.18 + intensity * 0.82})`
        const title = `${dayLabel} ${h}:00 — ${count} ${eventsWord}`
        return `<td class="reports-heatmap__cell" style="background:${bg}" title="${title}"></td>`
      }).join("")
      return `<tr><th class="reports-heatmap__day">${dayLabel}</th>${cells}</tr>`
    }).join("")

    this.heatmapTarget.innerHTML = `
      <div class="reports-heatmap">
        <table class="reports-heatmap__table" role="img" aria-label="heatmap">
          <thead><tr><th></th>${hourHeads}</tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="reports-heatmap__legend">
          <span>${this.L.legend_low || "Less"}</span>
          <span class="reports-heatmap__swatch reports-heatmap__swatch--0"></span>
          <span class="reports-heatmap__swatch reports-heatmap__swatch--1"></span>
          <span class="reports-heatmap__swatch reports-heatmap__swatch--2"></span>
          <span class="reports-heatmap__swatch reports-heatmap__swatch--3"></span>
          <span class="reports-heatmap__swatch reports-heatmap__swatch--4"></span>
          <span>${this.L.legend_high || "More"}</span>
        </div>
      </div>
    `
  }
}
